import { z } from "zod";

/** The longest custom round address; anything storing one must allow it. */
export const ROUND_SLUG_MAX_LENGTH = 80;

export const roundSlugSchema = z
  .string()
  .min(3)
  .max(ROUND_SLUG_MAX_LENGTH)
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    "Use lowercase letters, numbers, and single hyphens.",
  )
  .refine(
    (slug) => !slug.startsWith("r-"),
    "Addresses starting with r- are reserved for round IDs.",
  );

export function suggestRoundSlug(name: string): string {
  const slug = name
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80)
    .replace(/-$/, "");
  return roundSlugSchema.safeParse(slug).success ? slug : "";
}

export interface RoundSlugAddress {
  slug: string;
  roundId: string;
  isPrimary: boolean;
}

export function primaryRoundReference(
  roundId: string,
  addresses: readonly RoundSlugAddress[],
): string {
  return (
    addresses.find(
      (address) => address.roundId === roundId && address.isPrimary,
    )?.slug ?? roundId
  );
}

export function roundApplicationPath(application: {
  roundId: string;
  roundSlug?: string | null;
}): string {
  return `/apply/${encodeURIComponent(application.roundSlug ?? application.roundId)}`;
}

// Rewrite only the round segment; preserve encoded proposal names, query, hash,
// and trailing slash byte-for-byte. Legacy form-specific applicant URLs stay ID-based.
export function translateRoundPath(
  path: string,
  addresses: readonly RoundSlugAddress[],
  direction: "id" | "slug",
): string {
  return path.replace(
    /^\/(round|apply)\/([^/?#]+)(?=[/?#]|$)/,
    (prefix, surface: string, reference: string) => {
      if (
        surface === "apply" &&
        !/^\/apply\/[^/?#]+\/?(?:[?#].*)?$/.test(path)
      ) {
        return prefix;
      }

      const alias = addresses.find((address) => address.slug === reference);
      const roundId = alias?.roundId ?? reference;

      if (direction === "id") {
        return `/${surface}/${roundId}`;
      }

      return `/${surface}/${primaryRoundReference(roundId, addresses)}`;
    },
  );
}
